/**
 * Pure layout utilities for annotation overlays.
 *
 * Computes where to place annotation dots, leader lines, and label text in
 * 2D viewport space. No React or Three.js dependencies — only plain numbers.
 *
 * Visual anatomy (from the SVG design):
 *
 *   [dot] → 45° leader line (24px diagonal) → elbow → horizontal line
 *                                                        (label text width)
 *   Name text is above the horizontal line.
 *   Description text is below the horizontal line.
 *
 * Corner conventions:
 *   TL: dot at top-left of screen bounds, leader goes up-left  (−x, −y)
 *   TR: dot at top-right, leader goes up-right                  (+x, −y)
 *   BL: dot at bottom-left, leader goes down-left               (−x, +y)
 *   BR: dot at bottom-right, leader goes down-right             (+x, +y)
 *
 * The horizontal line extends away from the object (left for TL/BL,
 * right for TR/BR) by the width of the label text.
 */

export type Corner = 'TL' | 'TR' | 'BL' | 'BR';

/** Raw 2D data produced by AnnotationSensor for one annotated geometry. */
export type AnnotationRenderData = {
  name: string;
  description: string;
  alwaysVisible: boolean;
  /** Whether the geometry is visible enough (>= 25% in viewport) */
  visible: boolean;
  /** Whether the geometry mesh is currently hovered (for alwaysVisible=false) */
  hovered: boolean;
  /** Clipped screen bounding box in pixels */
  screenBounds: { left: number; top: number; right: number; bottom: number };
};

/** Fully computed layout for one annotation, ready to render. */
export type AnnotationLayout = {
  name: string;
  description: string;
  /** Pixel position of the dot */
  dotX: number;
  dotY: number;
  corner: Corner;
  /** End of the 45° leader line (= start of horizontal line) */
  elbowX: number;
  elbowY: number;
  /** End of the horizontal line (= text anchor point) */
  lineEndX: number;
  /** Y position of the text area (top of name text) */
  textTopY: number;
  /** SVG text-anchor for name/description */
  textAnchor: 'start' | 'end';
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Inward margin from the bounds corner to the dot position (px). */
const DOT_MARGIN = 8;

/** Diagonal length of the 45° leader line (px). */
const LEADER_DIAGONAL = 24;

/** The diagonal step per axis = LEADER_DIAGONAL / sqrt(2). */
const LEADER_STEP = Math.round(LEADER_DIAGONAL / Math.SQRT2); // ≈ 17px

/** Gap between the horizontal line and the text (px). */
const TEXT_LINE_GAP = 4;

/** Font size used for name text in the overlay (px). Matches CSS in AnnotationOverlay. */
const NAME_FONT_SIZE = 13;

/** Font size for description text (px). */
const DESC_FONT_SIZE = 11;

/** Padding to add around each label bounding box for overlap detection. */
const OVERLAP_PADDING = 8;

// ---------------------------------------------------------------------------
// Label width measurement
// ---------------------------------------------------------------------------

/** Canvas context used for text measurement (created once, lazily). */
let _measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (_measureCtx) return _measureCtx;
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) _measureCtx = ctx;
    return ctx;
  } catch {
    return null;
  }
}

/** Measure text width in pixels using a 2D canvas context. */
function measureText(text: string, fontSize: number, bold = false): number {
  const ctx = getMeasureCtx();
  if (!ctx) {
    // Fallback: approximate using character count and font size
    return text.length * fontSize * (bold ? 0.62 : 0.55);
  }
  ctx.font = `${bold ? '600 ' : ''}${fontSize}px system-ui, -apple-system, sans-serif`;
  return ctx.measureText(text).width;
}

/** Cache of label widths: key = `name|desc|fontSize` */
const widthCache = new Map<string, number>();

/**
 * Get the horizontal line length for an annotation.
 * This equals the width of the name text (the wider of name/desc is used).
 */
export function getLineWidth(name: string, description: string): number {
  const cacheKey = `${name}|${description}`;
  if (widthCache.has(cacheKey)) return widthCache.get(cacheKey)!;
  const nameW = measureText(name, NAME_FONT_SIZE, true);
  const descW = description ? measureText(description, DESC_FONT_SIZE) : 0;
  const w = Math.max(nameW, descW) + 4; // 4px padding
  widthCache.set(cacheKey, w);
  return w;
}

// ---------------------------------------------------------------------------
// Corner selection
// ---------------------------------------------------------------------------

/**
 * Pick the "natural" corner for an annotation based on which viewport edge
 * the object is closest to. The dot is placed at that corner of the screen
 * bounds and the leader line points outward toward that edge.
 */
function naturalCorner(
  bounds: { left: number; top: number; right: number; bottom: number },
  viewportWidth: number,
  viewportHeight: number,
): Corner {
  const cx = (bounds.left + bounds.right) / 2;
  const cy = (bounds.top + bounds.bottom) / 2;

  // Distance from each viewport edge
  const distLeft = cx;
  const distRight = viewportWidth - cx;
  const distTop = cy;
  const distBottom = viewportHeight - cy;

  // Pick the nearest horizontal and vertical edges
  const nearLeft = distLeft <= distRight;
  const nearTop = distTop <= distBottom;

  if (nearLeft && nearTop) return 'TL';
  if (!nearLeft && nearTop) return 'TR';
  if (nearLeft && !nearTop) return 'BL';
  return 'BR';
}

/**
 * All 4 corners in priority order starting from the natural one.
 * We try natural first, then cycle through the others.
 */
function cornersInPriorityOrder(natural: Corner): Corner[] {
  const all: Corner[] = ['TL', 'TR', 'BL', 'BR'];
  const rest = all.filter((c) => c !== natural);
  return [natural, ...rest];
}

// ---------------------------------------------------------------------------
// Dot and line geometry
// ---------------------------------------------------------------------------

/** Compute dot position from corner + bounds + margin. */
function dotPosition(
  corner: Corner,
  bounds: { left: number; top: number; right: number; bottom: number },
): { x: number; y: number } {
  switch (corner) {
    case 'TL':
      return { x: bounds.left + DOT_MARGIN, y: bounds.top + DOT_MARGIN };
    case 'TR':
      return { x: bounds.right - DOT_MARGIN, y: bounds.top + DOT_MARGIN };
    case 'BL':
      return { x: bounds.left + DOT_MARGIN, y: bounds.bottom - DOT_MARGIN };
    case 'BR':
      return { x: bounds.right - DOT_MARGIN, y: bounds.bottom - DOT_MARGIN };
  }
}

/** Compute elbow position (end of 45° leader). */
function elbowPosition(
  corner: Corner,
  dot: { x: number; y: number },
): { x: number; y: number } {
  switch (corner) {
    case 'TL':
      return { x: dot.x - LEADER_STEP, y: dot.y - LEADER_STEP };
    case 'TR':
      return { x: dot.x + LEADER_STEP, y: dot.y - LEADER_STEP };
    case 'BL':
      return { x: dot.x - LEADER_STEP, y: dot.y + LEADER_STEP };
    case 'BR':
      return { x: dot.x + LEADER_STEP, y: dot.y + LEADER_STEP };
  }
}

/** Whether the horizontal line extends to the left or right from the elbow. */
function lineDirection(corner: Corner): 'left' | 'right' {
  return corner === 'TL' || corner === 'BL' ? 'left' : 'right';
}

/** Compute end of horizontal line from elbow + direction + width. */
function lineEnd(
  elbow: { x: number; y: number },
  direction: 'left' | 'right',
  lineWidth: number,
): number {
  return direction === 'left' ? elbow.x - lineWidth : elbow.x + lineWidth;
}

// ---------------------------------------------------------------------------
// Label bounding box (for overlap detection)
// ---------------------------------------------------------------------------

type Rect = { left: number; top: number; right: number; bottom: number };

function labelBounds(layout: {
  elbowX: number;
  elbowY: number;
  lineEndX: number;
  textTopY: number;
  textAnchor: 'start' | 'end';
  name: string;
  description: string;
}): Rect {
  const { elbowX, elbowY, lineEndX, textTopY, name, description } = layout;

  // The label area spans from the horizontal line (at elbowY) upward (for name)
  // and downward (for description), horizontally between elbowX and lineEndX.
  const labelHeight =
    NAME_FONT_SIZE +
    TEXT_LINE_GAP +
    (description ? DESC_FONT_SIZE + TEXT_LINE_GAP : 0);

  const left = Math.min(elbowX, lineEndX) - OVERLAP_PADDING;
  const right = Math.max(elbowX, lineEndX) + OVERLAP_PADDING;
  const top = textTopY - OVERLAP_PADDING;
  const bottom = elbowY + labelHeight + OVERLAP_PADDING;

  return { left, top, right, bottom };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top
  );
}

/**
 * Compute how many pixels of a label rect overflow the viewport.
 * Returns 0 when the rect is fully inside the viewport.
 */
function viewportOverflow(
  lb: Rect,
  viewportWidth: number,
  viewportHeight: number,
): number {
  let overflow = 0;
  if (lb.left < 0) overflow += -lb.left;
  if (lb.top < 0) overflow += -lb.top;
  if (lb.right > viewportWidth) overflow += lb.right - viewportWidth;
  if (lb.bottom > viewportHeight) overflow += lb.bottom - viewportHeight;
  return overflow;
}

/**
 * Clamp an annotation layout so the text label stays fully inside the
 * viewport.  The dot stays anchored to the object; only the elbow,
 * horizontal line, and text are shifted when they would otherwise
 * extend outside the visible area.
 */
function clampLayoutToViewport(
  layout: AnnotationLayout,
  viewportWidth: number,
  viewportHeight: number,
): AnnotationLayout {
  const isGoingUp = layout.corner === 'TL' || layout.corner === 'TR';
  const totalTextHeight =
    NAME_FONT_SIZE +
    TEXT_LINE_GAP +
    (layout.description ? DESC_FONT_SIZE + TEXT_LINE_GAP : 0);

  // Compute the full visual rect of the text + horizontal line area
  // (excluding the dot and 45° leader which can be clipped harmlessly).
  const textLeft = Math.min(layout.elbowX, layout.lineEndX);
  const textRight = Math.max(layout.elbowX, layout.lineEndX);
  const textTop = isGoingUp
    ? layout.elbowY - totalTextHeight - TEXT_LINE_GAP
    : layout.elbowY;
  const textBottom = isGoingUp
    ? layout.elbowY
    : layout.elbowY + TEXT_LINE_GAP + totalTextHeight;

  let dx = 0;
  let dy = 0;

  // Horizontal clamping
  if (textLeft < 0) dx = -textLeft;
  else if (textRight > viewportWidth) dx = viewportWidth - textRight;

  // Vertical clamping
  if (textTop < 0) dy = -textTop;
  else if (textBottom > viewportHeight) dy = viewportHeight - textBottom;

  if (dx === 0 && dy === 0) return layout;

  return {
    ...layout,
    elbowX: layout.elbowX + dx,
    elbowY: layout.elbowY + dy,
    lineEndX: layout.lineEndX + dx,
    textTopY: layout.textTopY + dy,
  };
}

// ---------------------------------------------------------------------------
// Main layout computation
// ---------------------------------------------------------------------------

/**
 * Compute final layout for all visible annotations, applying corner
 * selection and greedy anti-overlap.
 *
 * Sorting: largest screen area first (higher priority = placed first).
 */
export function computeAnnotationLayouts(
  items: AnnotationRenderData[],
  viewportWidth: number,
  viewportHeight: number,
): AnnotationLayout[] {
  // Filter to only items that should be shown
  const visible = items.filter(
    (item) => item.visible && (item.alwaysVisible || item.hovered),
  );

  if (visible.length === 0) return [];

  // Sort by screen area descending (larger objects get priority)
  const sorted = [...visible].sort((a, b) => {
    const areaA =
      (a.screenBounds.right - a.screenBounds.left) *
      (a.screenBounds.bottom - a.screenBounds.top);
    const areaB =
      (b.screenBounds.right - b.screenBounds.left) *
      (b.screenBounds.bottom - b.screenBounds.top);
    return areaB - areaA;
  });

  const results: AnnotationLayout[] = [];
  const placedBounds: Rect[] = [];

  for (const item of sorted) {
    const lineWidth = getLineWidth(item.name, item.description);
    const natural = naturalCorner(
      item.screenBounds,
      viewportWidth,
      viewportHeight,
    );
    const candidates = cornersInPriorityOrder(natural);

    let bestLayout: AnnotationLayout | null = null;
    let bestScore = -Infinity; // higher is better

    for (const corner of candidates) {
      const dot = dotPosition(corner, item.screenBounds);
      const elbow = elbowPosition(corner, dot);
      const direction = lineDirection(corner);
      const endX = lineEnd(elbow, direction, lineWidth);

      // Text is above the horizontal line for TL/TR corners (leader goes up)
      // and below the horizontal line for BL/BR corners (leader goes down).
      // In all cases name sits just above the horizontal line.
      const isGoingUp = corner === 'TL' || corner === 'TR';
      const totalTextHeight =
        NAME_FONT_SIZE +
        TEXT_LINE_GAP +
        (item.description ? DESC_FONT_SIZE + TEXT_LINE_GAP : 0);

      // textTopY: the top edge of the name text
      const textTopY = isGoingUp
        ? elbow.y - totalTextHeight - TEXT_LINE_GAP
        : elbow.y + TEXT_LINE_GAP;

      const layout: AnnotationLayout = {
        name: item.name,
        description: item.description,
        dotX: dot.x,
        dotY: dot.y,
        corner,
        elbowX: elbow.x,
        elbowY: elbow.y,
        lineEndX: endX,
        textTopY,
        // Text is anchored at lineEndX and extends BACK toward the object:
        // left-going line → lineEndX is leftmost → anchor 'start' so text goes right
        // right-going line → lineEndX is rightmost → anchor 'end' so text goes left
        textAnchor: direction === 'left' ? 'start' : 'end',
      };

      const lb = labelBounds(layout);

      // Count how many already-placed annotations this overlaps
      const overlapCount = placedBounds.filter((b) =>
        rectsOverlap(lb, b),
      ).length;

      // Measure how far the label extends outside the viewport (px)
      const overflow = viewportOverflow(lb, viewportWidth, viewportHeight);

      // Score: prefer in-viewport (overflow == 0) first, then fewest
      // overlaps.  Each pixel of overflow costs 1000 "overlap units" so
      // even a single pixel outside the viewport is worse than several
      // overlaps with other labels.
      const score = -(overlapCount * 1 + overflow * 1000);

      if (overlapCount === 0 && overflow === 0) {
        // Perfect placement — use immediately
        bestLayout = layout;
        break;
      }

      if (score > bestScore) {
        bestScore = score;
        bestLayout = layout;
      }
    }

    if (bestLayout) {
      // Safety net: clamp to viewport even if the best corner still overflows
      bestLayout = clampLayoutToViewport(
        bestLayout,
        viewportWidth,
        viewportHeight,
      );
      results.push(bestLayout);
      placedBounds.push(labelBounds(bestLayout));
    }
  }

  return results;
}
